package com.exura.features.plandefinition.presentation

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.silk.components.text.SpanText
import com.exura.components.loading.Loader
import com.exura.components.pageTypography.ExuraBodySizeTextVariant
import com.exura.components.pageTypography.ExuraTextStyle
import com.exura.features.firebase.domain.model.versioning.Version
import com.exura.features.plandefinition.domain.model.metadata.PlanDefinitionMetadata
import com.exura.features.plandefinition.domain.model.plan.BasePlan
import com.exura.features.plandefinition.planLocalization
import com.exura.features.plandefinition.usecase.ListTreatmentPlansUseCase
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

@Composable
internal fun PlanDefinitionListSelectionDialogContent(
    onPlanSelected: (Version<BasePlan, PlanDefinitionMetadata>) -> Unit,
) {
    val scope = rememberCoroutineScope()
    val controller = rememberPlanListController(scope)
    val state = controller.uiState

    Box(
        modifier = Modifier
            .fillMaxSize()
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {

        when {
            state.isLoading && state.plans.isEmpty() -> Loader()
            state.error != null -> SpanText(
                text = state.error,
                modifier = ExuraTextStyle.toModifier(ExuraBodySizeTextVariant),
            )
            state.plans.isEmpty() -> SpanText(
                text = planLocalization.current.noPlansAvailable,
                modifier = ExuraTextStyle.toModifier(ExuraBodySizeTextVariant),
            )
            else -> Column(modifier = Modifier.fillMaxWidth()) {
                state.plans.forEach { plan ->
                    PlanDefinitionListEntry(
                        version = plan,
                        onClick = {
                            onPlanSelected(plan)
                        }
                    )
                }
            }

        }
    }
}

private data class PlanListUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val plans: List<Version<BasePlan, PlanDefinitionMetadata>> = emptyList()
)

private class PlanListController(
    private val scope: CoroutineScope,
    private val listTreatmentPlansUseCase: ListTreatmentPlansUseCase,
) {
    var uiState by mutableStateOf(PlanListUiState())
        private set

    private var loadJob: Job? = null

    init {
        loadPlans()
    }

    private fun loadPlans() {
        loadJob?.cancel()

        loadJob = scope.launch {
            uiState = uiState.copy(isLoading = true, error = null)

            listTreatmentPlansUseCase()
                .onSuccess { flow ->
                    flow.collect { plans ->
                        uiState = uiState.copy(
                            isLoading = false,
                            plans = plans
                        )
                    }
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isLoading = false,
                        error = error.message
                    )
                }
        }
    }
}

@Composable
private fun rememberPlanListController(
    scope: CoroutineScope,
): PlanListController {
    val listTreatmentPlansUseCase = injectAndRemember<ListTreatmentPlansUseCase>()

    return remember(scope) {
        PlanListController(
            scope = scope,
            listTreatmentPlansUseCase = listTreatmentPlansUseCase,
        )
    }
}
